"use client";
// Imports
import { cva } from "class-variance-authority";
import Link from "next/link";
import { HeaderNav, Banner, MobileMenu } from "@/components";
import { LazySearchDrawer } from "@/components/global/drawers/LazySearchDrawer";
import { useEffect, useState } from "react";
import useStore from "@/lib/state";
import { Logo } from "./Logo";
import Image from "next/image";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useKindeSession } from "@/components/KindeWrapper";
import { LogoutLink } from "@kinde-oss/kinde-auth-nextjs/components";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

// Styles
const headerStyles = cva(
  "flex px-4 py-3 md:px-6 lg:px-8 lg:py-4 items-center justify-center",
  {
    variants: {
      showBanner: {
        true: "",
        false: "",
      },
    },
  }
);

// Header
export function Header({ data, banner, showDates }) {
  const { showBanner } = useStore();
  const { user } = useKindeSession();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [subMenu, setSubMenu] = useState({ open: false, index: null });

  useEffect(() => {
    let lastScrollTop = 0;
    const header = document.querySelector("header");

    window.addEventListener("scroll", function () {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop > 150) {
        header.classList.add("scrolled");
      } else {
        header.classList.remove("scrolled");
      }
      lastScrollTop = scrollTop;
    });

    return () => {
      window.removeEventListener("scroll", function () {});
    };
  }, []);

  // Render
  return (
    <header
      className={data?.showModeEnabled ? "font-show-heading" : "font-heading"}
    >
      <Banner banner={banner} />
      <div className={`${headerStyles({ showBanner })}`}>
        <div className="grid grid-cols-[auto_1fr_auto] items-center w-full gap-4 lg:gap-6">
          <div className="flex items-center justify-start shrink-0">
            <Logo logo={data?.mainLogo} />
          </div>
          <div className="flex items-center justify-center min-w-0">
            <HeaderNav showDates={showDates} data={data} user={user} />
          </div>
          <div className="flex items-center justify-end gap-3 lg:gap-4 level-4 shrink-0">
            {data?.showBuyTickets && data?.buyTicketsLink && (
              <Link
                href={data.buyTicketsLink}
                className={cn(
                  buttonVariants({ size: "default", variant: "default" }),
                  "inline-flex items-center justify-center h-9 px-4 text-sm 2xl:h-11 2xl:px-6 2xl:text-base",
                  "font-heading !uppercase tracking-normal leading-normal"
                )}
              >
                Buy Tickets
              </Link>
            )}
            <LazySearchDrawer />
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <div className="relative w-10 h-10">
                    <div className="flex items-center justify-center w-10 h-10 text-white rounded-full bg-brand-orange">
                      {user?.given_name.slice(0, 1) +
                        user?.family_name.slice(0, 1)}
                    </div>
                    {user?.picture && (
                      <Image
                        src={user?.picture}
                        alt={user?.given_name + " " + user?.family_name}
                        width={40}
                        height={40}
                        className="absolute inset-0 rounded-full"
                      />
                    )}
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-brand-wool">
                  <DropdownMenuLabel>
                    {user?.given_name + " " + user?.family_name}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-background" />
                  <DropdownMenuLabel>
                    <LogoutLink>Logout</LogoutLink>
                  </DropdownMenuLabel>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <MobileMenu data={data} />
          </div>
        </div>
      </div>
    </header>
  );
}
